import React, { lazy, Suspense, useEffect } from 'react';

// context
import { useAuth } from 'context/authContext';

// screens
import { LoadingScreen } from 'pages/loadingScreen';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';
import ApiProvider from 'context/apiContext';
import VersionWatcher from 'hooks/versionWatcher';
import { BrowserRouter as Router } from 'react-router-dom';
import { safeLocalStorage } from 'utils/safeLocalStorage';
import ErrorBoundary from 'components/ErrorBoundary';

const Dashboard = lazy(() => import('./pages/index'));
const ParkingHome = lazy(() => import('./pages/loggedIn/reception/screens'));
const HomePage = lazy(() => import('./pages/public'));

const PUBLIC_POST_LOGIN_PATHS = new Set(['/', '/login', '/auth-redirect']);

const App = () => {
    const data = useAuth();
    useEffect(() => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        return () => {
            const currentDate = new Date();
            safeLocalStorage.setItem('tabOpenTime', currentDate.toString());
        };
    }, [data.isAuthenticated, data.language, data.id, data.organisationID]);

    const isReception =
        data.isAuthenticated &&
        Object.keys(data.userPermissions)?.includes('Reception');

    const isBackofficeUser =
        data.isAuthenticated && Object.keys(data.userPermissions)?.length > 0;

    useEffect(() => {
        if (!isBackofficeUser || isReception) {
            return;
        }
        if (PUBLIC_POST_LOGIN_PATHS.has(window.location.pathname)) {
            window.location.replace('/dashboard');
        }
    }, [isBackofficeUser, isReception]);

    if (!data.authHydrated) {
        return (
            <ErrorBoundary>
                <LoadingScreen />
            </ErrorBoundary>
        );
    }

    return (
        <ErrorBoundary>
            <Suspense fallback={<LoadingScreen />}>
                <ToastContainer
                    position="top-right"
                    autoClose={2000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    theme="light"
                />
                {data.isAuthenticated &&
                Object.keys(data.userPermissions)?.length ? (
                    Object.keys(data.userPermissions).includes('Reception') ? (
                        <Router>
                            <ParkingHome />
                        </Router>
                    ) : (
                        <ApiProvider>
                            <Dashboard />
                        </ApiProvider>
                    )
                ) : (
                    <HomePage isLoading={data.loadingState} />
                )}
                <VersionWatcher autoReload={isReception} />
            </Suspense>
        </ErrorBoundary>
    );
};

export default App;
